import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router";
import { api } from "../api";
import { PocketFrame } from "../shell";
import { APPLICATION_STATUS_LABELS, type ApplicationStatus } from "../../domain/applications";

type ApiErr = Error & { status?: number; body?: { title?: string; detail?: string; code?: string } };

function isStepUpError(err: unknown): boolean {
  const e = err as ApiErr;
  if (e.status !== 401 && e.status !== 403) return false;
  return e.body?.title === "Step-up" || e.body?.code === "step_up";
}

function humanError(err: unknown): string {
  const e = err as ApiErr;
  const detail = e.body?.detail;
  if (typeof detail === "string" && detail.trim() && !detail.trim().startsWith("{")) return detail;
  if (e.message && e.message !== "request failed" && !e.message.trim().startsWith("{")) return e.message;
  return "Не удалось выполнить действие.";
}

type AdminApi = <T>(path: string, init?: RequestInit) => Promise<T>;
const AdminApiContext = createContext<AdminApi>(api);
function useAdminApi() {
  return useContext(AdminApiContext);
}

function StepUpProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{
    path: string;
    init: RequestInit;
    resolve: (value: unknown) => void;
    reject: (err: unknown) => void;
  } | null>(null);
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const run = useCallback<AdminApi>(async (path, init = {}) => {
    try {
      return await api(path, init);
    } catch (ex) {
      if (!isStepUpError(ex)) throw ex;
      return await new Promise((resolve, reject) => {
        setPassword("");
        setErr(humanError(ex));
        setPending({ path, init, resolve: (value) => resolve(value as never), reject });
      });
    }
  }, []);

  return (
    <AdminApiContext.Provider value={run}>
      {pending ? (
        <section className="card" aria-labelledby="admin-stepup-title">
          <h2 id="admin-stepup-title">Подтвердите вход</h2>
          <p>Чтобы изменить данные, введите пароль аккаунта. Код TOTP уже подтверждён.</p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              setErr("");
              try {
                await api("/api/v1/admin/step-up", { method: "POST", body: JSON.stringify({ password }) });
                const result = await api(pending.path, pending.init);
                pending.resolve(result);
                setPending(null);
                setPassword("");
              } catch (ex) {
                const message = humanError(ex);
                if (isStepUpError(ex)) {
                  setErr(message);
                } else {
                  pending.reject(ex);
                  setPending(null);
                  setErr(message);
                }
              } finally {
                setBusy(false);
              }
            }}
          >
            <label className="field">
              Пароль
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <button className="btn steel" type="submit" disabled={busy}>Подтвердить</button>
          </form>
          {err ? <p className="alert">{err}</p> : null}
        </section>
      ) : null}
      {children}
    </AdminApiContext.Provider>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <PocketFrame wide className="admin-cabinet">
      <div className="shell">
        <aside className="side">
          <div className="brand">Clever · Админ</div>
          <nav className="nav">
            <NavLink to="/admin" end>Обзор</NavLink>
            <NavLink to="/admin/users">Пользователи</NavLink>
            <NavLink to="/admin/sellers">Продавцы</NavLink>
            <NavLink to="/admin/trainers">Тренажёры</NavLink>
            <NavLink to="/admin/applications">Заявки</NavLink>
            <NavLink to="/admin/ledger">Транзакции</NavLink>
            <NavLink to="/admin/commission">Комиссия</NavLink>
            <NavLink to="/admin/audit">Аудит</NavLink>
            <NavLink to="/admin/totp">TOTP</NavLink>
          </nav>
        </aside>
        <main className="main cabinet">{children}</main>
      </div>
    </PocketFrame>
  );
}

export function TotpPage() {
  const [me, setMe] = useState<{ email?: string } | null | undefined>(undefined);
  const [state, setState] = useState<{ granted: boolean; confirmed: boolean; needsSetup: boolean } | null>(null);
  const [secret, setSecret] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const reload = () => api<NonNullable<typeof state>>("/api/v1/admin/totp").then(setState).catch(() => setState(null));
  useEffect(() => {
    api<{ email?: string }>("/api/v1/me").then(setMe).catch(() => setMe(null));
    reload();
  }, []);
  if (me === undefined) return <p className="page">…</p>;
  if (!me) return <Navigate to="/login?next=/admin/totp" />;
  return (
    <PocketFrame className="totp-page">
    <div className="page">
      <h1>Одноразовый код администратора</h1>
      <p>Вход в админ-кабинет требует TOTP. Bootstrap выполняется только в консоли сервера и не создаёт пароль.</p>
      {!state?.granted ? (
        <p>У вашей учётной записи нет роли администратора. Попросите оператора выполнить TTY-команду повышения.</p>
      ) : state.confirmed && !secret ? (
        <p>TOTP уже подтверждён. Можно открыть <a href="/admin">кабинет администратора</a>.</p>
      ) : (
        <>
          <p>Добавьте секрет в приложение-аутентификатор, затем введите шестизначный код.</p>
          <button
            className="btn"
            type="button"
            onClick={async () => {
              setErr("");
              try {
                const r = await api<{ secret: string; otpauth: string }>("/api/v1/admin/totp/setup", { method: "POST", body: "{}" });
                setSecret(r.secret);
                setOtpauth(r.otpauth);
              } catch (ex) {
                setErr((ex as Error).message);
              }
            }}
          >Создать секрет</button>
          {secret ? (
            <section className="card">
              <label className="field">Секрет<input readOnly value={secret} /></label>
              <label className="field">Ссылка для приложения<input readOnly value={otpauth} /></label>
              <p className="muted">Сохраните секрет в аутентификаторе. Страница не показывает сырой JSON.</p>
            </section>
          ) : null}
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setErr("");
              try {
                await api("/api/v1/admin/totp/confirm", { method: "POST", body: JSON.stringify({ code }) });
                setMsg("Код подтверждён. Теперь можно открыть кабинет.");
                setSecret("");
                reload();
              } catch (ex) {
                setErr((ex as Error).message);
              }
            }}
          >
            <label className="field">Код из приложения<input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} /></label>
            <button className="btn steel" type="submit">Подтвердить</button>
          </form>
        </>
      )}
      {err ? <p className="alert">{err}</p> : null}
      {msg ? <p>{msg}</p> : null}
    </div>
    </PocketFrame>
  );
}

function Overview() {
  const [data, setData] = useState<{ users: number; sellers: number; trainers: number; applications: Array<{ status: string; n: number }> } | null>(null);
  useEffect(() => {
    api<NonNullable<typeof data>>("/api/v1/admin/overview").then(setData).catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Очереди</h1>
      <div className="grid3">
        <section className="card"><h2>Пользователи</h2><p>{data?.users ?? "—"}</p></section>
        <section className="card"><h2>Продавцы</h2><p>{data?.sellers ?? "—"}</p></section>
        <section className="card"><h2>Тренажёры</h2><p>{data?.trainers ?? "—"}</p></section>
      </div>
      <h2>Заявки</h2>
      <ul>{data?.applications.map((a) => <li key={a.status}>{APPLICATION_STATUS_LABELS[a.status as ApplicationStatus] ?? a.status}: {a.n}</li>)}</ul>
    </>
  );
}

function UsersPage() {
  const adminApi = useAdminApi();
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Array<{ id: string; emailDisplay: string; disabled: boolean; roles: string[] }>>([]);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const load = () => api<{ items: typeof items }>(`/api/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((d) => setItems(d.items));
  useEffect(() => { load(); }, []);
  return (
    <>
      <h1>Пользователи и роли</h1>
      <form onSubmit={(e) => { e.preventDefault(); load(); }}>
        <label className="field">Точный email<input value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <button className="btn" type="submit">Найти</button>
      </form>
      <table className="progress-table">
        <thead><tr><th>Email</th><th>Роли</th><th></th></tr></thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id}>
              <td>{u.emailDisplay}{u.disabled ? " · отключён" : ""}</td>
              <td>{u.roles.join(", ") || "—"}</td>
              <td>
                <button
                  className="btn ghost"
                  type="button"
                  onClick={async () => {
                    setErr("");
                    try {
                      await adminApi(`/api/v1/admin/users/${u.id}/disable`, { method: "POST", body: JSON.stringify({ reason }) });
                      load();
                    } catch (ex) {
                      setErr(humanError(ex));
                    }
                  }}
                >Отключить</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="field">Причина<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      <button
        className="btn"
        type="button"
        onClick={async () => {
          setErr("");
          try {
            await adminApi("/api/v1/admin/grants", { method: "POST", body: JSON.stringify({ email: q, reason }) });
            load();
          } catch (ex) {
            setErr(humanError(ex));
          }
        }}
      >Выдать роль администратора</button>
      {err ? <p className="alert">{err}</p> : null}
    </>
  );
}

function SellersPage() {
  const adminApi = useAdminApi();
  const [items, setItems] = useState<Array<{ id: string; display_name: string; status: string; demo: boolean; slug: string | null; owner_email: string }>>([]);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const load = () => api<{ items: typeof items }>("/api/v1/admin/sellers").then((d) => setItems(d.items));
  useEffect(() => { load(); }, []);
  return (
    <>
      <h1>Репетиторы и moderation</h1>
      {items.map((s) => (
        <article key={s.id} className="card">
          <h2>{s.display_name} {s.demo ? <span className="badge">Демонстрационный профиль</span> : null}</h2>
          <p>Адрес: {s.slug ?? "—"}. Статус: {s.status}. Владелец: {s.owner_email}</p>
          <div className="row">
            {["pending", "published", "hidden", "suspended"].map((status) => (
              <button
                key={status}
                className="btn ghost"
                type="button"
                onClick={async () => {
                  setErr("");
                  try {
                    await adminApi(`/api/v1/admin/sellers/${s.id}/status`, { method: "POST", body: JSON.stringify({ status, reason }) });
                    load();
                  } catch (ex) {
                    setErr(humanError(ex));
                  }
                }}
              >{status}</button>
            ))}
          </div>
        </article>
      ))}
      <label className="field">Причина действия<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      {err ? <p className="alert">{err}</p> : null}
    </>
  );
}

function TrainersPage() {
  const adminApi = useAdminApi();
  const [trainers, setTrainers] = useState<Array<{ id: string; slug: string; title: string; owner_seller_id: string | null; owner_name: string | null }>>([]);
  const [sellers, setSellers] = useState<Array<{ id: string; display_name: string }>>([]);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    api<{ items: typeof trainers }>("/api/v1/admin/trainers").then((d) => setTrainers(d.items));
    api<{ items: typeof sellers }>("/api/v1/admin/sellers").then((d) => setSellers(d.items));
  }, []);
  return (
    <>
      <h1>Тренажёры и владельцы</h1>
      {trainers.map((t) => (
        <article key={t.id} className="card">
          <h2>{t.title}</h2>
          <p>Владелец: {t.owner_name ?? "не назначен"}</p>
          <label className="field">
            Назначить владельца
            <select
              defaultValue={t.owner_seller_id ?? ""}
              onChange={async (e) => {
                setErr("");
                try {
                  await adminApi(`/api/v1/admin/trainers/${t.id}/owner`, {
                    method: "POST",
                    body: JSON.stringify({ sellerId: e.target.value, reason }),
                  });
                } catch (ex) {
                  setErr(humanError(ex));
                }
              }}
            >
              <option value="">—</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
            </select>
          </label>
        </article>
      ))}
      <label className="field">Причина<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      {err ? <p className="alert">{err}</p> : null}
    </>
  );
}

function ApplicationsPage() {
  const adminApi = useAdminApi();
  const [items, setItems] = useState<Array<{ id: string; status: ApplicationStatus; statusLabel: string; seller_name: string }>>([]);
  const [open, setOpen] = useState<{ id: string; brief: Record<string, string>; status: ApplicationStatus } | null>(null);
  const [comment, setComment] = useState("");
  const [err, setErr] = useState("");
  const load = () => api<{ items: typeof items }>("/api/v1/admin/applications").then((d) => setItems(d.items));
  useEffect(() => { load(); }, []);
  const nexts: Record<string, ApplicationStatus[]> = {
    submitted: ["clarification", "accepted", "rejected"],
    accepted: ["clarification", "in_progress", "rejected"],
    in_progress: ["clarification", "ready", "rejected"],
  };
  return (
    <>
      <h1>Заявки</h1>
      {items.map((a) => (
        <article key={a.id} className="card">
          <p>{a.seller_name} · {a.statusLabel}</p>
          <button className="btn ghost" type="button" onClick={async () => setOpen(await api(`/api/v1/admin/applications/${a.id}`))}>Открыть</button>
          {(nexts[a.status] ?? []).map((to) => (
            <button
              key={to}
              className="btn ghost"
              type="button"
              onClick={async () => {
                setErr("");
                try {
                  await adminApi(`/api/v1/admin/applications/${a.id}/transition`, {
                    method: "POST",
                    body: JSON.stringify({ to, comment }),
                  });
                  load();
                } catch (ex) {
                  setErr(humanError(ex));
                }
              }}
            >{APPLICATION_STATUS_LABELS[to]}</button>
          ))}
        </article>
      ))}
      <label className="field">Комментарий к переходу<textarea value={comment} onChange={(e) => setComment(e.target.value)} /></label>
      {err ? <p className="alert">{err}</p> : null}
      {open ? (
        <section className="card">
          <h2>Бриф</h2>
          {Object.entries(open.brief).map(([k, v]) => <p key={k}><strong>{k}:</strong> {String(v)}</p>)}
        </section>
      ) : null}
    </>
  );
}

function LedgerPage() {
  const adminApi = useAdminApi();
  const [items, setItems] = useState<Array<{ id: string; orderId: string | null; createdAt: string; postings: Array<{ account: string; amountKopecks: string }> }>>([]);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const load = () => api<{ items: typeof items }>("/api/v1/admin/ledger").then((d) => setItems(d.items));
  useEffect(() => { load(); }, []);
  return (
    <>
      <h1>Тестовые операции и ledger</h1>
      <p>История не редактируется. Компенсация добавляет обратную проводку.</p>
      {items.map((tx) => (
        <article key={tx.id} className="card">
          <p>Операция {tx.id.slice(0, 8)} · заказ {tx.orderId ? tx.orderId.slice(0, 8) : "компенсация"}</p>
          <ul>{tx.postings.map((p, i) => <li key={i}>{p.account}: {p.amountKopecks}</li>)}</ul>
          <button
            className="btn ghost"
            type="button"
            onClick={async () => {
              setErr("");
              try {
                await adminApi("/api/v1/admin/ledger/compensate", { method: "POST", body: JSON.stringify({ transactionId: tx.id, reason }) });
                load();
              } catch (ex) {
                setErr(humanError(ex));
              }
            }}
          >Компенсировать</button>
        </article>
      ))}
      <label className="field">Причина<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      {err ? <p className="alert">{err}</p> : null}
    </>
  );
}

function CommissionPage() {
  const adminApi = useAdminApi();
  const [sellers, setSellers] = useState<Array<{ id: string; display_name: string }>>([]);
  const [items, setItems] = useState<Array<{ seller_id: string; rate_bps: number; effective_from: string; reason: string }>>([]);
  const [sellerId, setSellerId] = useState("");
  const [rate, setRate] = useState("2000");
  const [when, setWhen] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const load = () => api<{ items: typeof items }>("/api/v1/admin/commission").then((d) => setItems(d.items));
  useEffect(() => {
    api<{ items: typeof sellers }>("/api/v1/admin/sellers").then((d) => setSellers(d.items));
    load();
  }, []);
  return (
    <>
      <h1>Комиссия</h1>
      <p>Новая ставка действует только с будущей даты. Исторические заказы не меняются.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          try {
            await adminApi("/api/v1/admin/commission", {
              method: "POST",
              body: JSON.stringify({ sellerId, rateBps: Number(rate), effectiveFrom: when ? new Date(when).toISOString() : "", reason }),
            });
            load();
          } catch (ex) {
            setErr(humanError(ex));
          }
        }}
      >
        <label className="field">Продавец
          <select value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
            <option value="">—</option>
            {sellers.map((s) => <option key={s.id} value={s.id}>{s.display_name}</option>)}
          </select>
        </label>
        <label className="field">Ставка, bps<input value={rate} onChange={(e) => setRate(e.target.value)} /></label>
        <label className="field">Начало<input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></label>
        <label className="field">Причина<input value={reason} onChange={(e) => setReason(e.target.value)} /></label>
        <button className="btn" type="submit">Назначить</button>
      </form>
      {err ? <p className="alert">{err}</p> : null}
      <table className="progress-table">
        <tbody>
          {items.map((r, i) => <tr key={i}><td>{r.seller_id.slice(0, 8)}</td><td>{r.rate_bps}</td><td>{r.effective_from}</td><td>{r.reason}</td></tr>)}
        </tbody>
      </table>
    </>
  );
}

function AuditPage() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<Array<{ id: string; action: string; target: string; reason: string; created_at: string }>>([]);
  const load = () => api<{ items: typeof items }>(`/api/v1/admin/audit${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((d) => setItems(d.items));
  useEffect(() => { load(); }, []);
  return (
    <>
      <h1>Журнал аудита</h1>
      <form onSubmit={(e) => { e.preventDefault(); load(); }}>
        <label className="field">Поиск<input value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <button className="btn" type="submit">Найти</button>
      </form>
      <table className="progress-table">
        <thead><tr><th>Действие</th><th>Объект</th><th>Причина</th></tr></thead>
        <tbody>
          {items.map((r) => <tr key={r.id}><td>{r.action}</td><td>{r.target}</td><td>{r.reason}</td></tr>)}
        </tbody>
      </table>
    </>
  );
}

export function AdminCabinet() {
  const [me, setMe] = useState<{ roles?: string[] } | null | undefined>(undefined);
  const [totp, setTotp] = useState<{ granted: boolean; confirmed: boolean } | null>(null);
  useEffect(() => {
    api<{ roles: string[] }>("/api/v1/me").then(setMe).catch(() => setMe(null));
    api<{ granted: boolean; confirmed: boolean }>("/api/v1/admin/totp").then(setTotp).catch(() => setTotp(null));
  }, []);
  if (me === undefined) return <p className="page">…</p>;
  if (!me) return <Navigate to="/login?next=/admin" />;
  if (!totp?.confirmed) return <Navigate to="/admin/totp" />;
  return (
    <StepUpProvider>
      <Shell>
        <Routes>
          <Route index element={<Overview />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="sellers" element={<SellersPage />} />
          <Route path="trainers" element={<TrainersPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="ledger" element={<LedgerPage />} />
          <Route path="commission" element={<CommissionPage />} />
          <Route path="audit" element={<AuditPage />} />
        </Routes>
      </Shell>
    </StepUpProvider>
  );
}
